<template>
    <div class="sip-subscription">
        <sip-page-intro
            :title="trans('subscription.subscription')"
            badge="Subscription"
            badge-tone="emerald"
            :breadcrumbs="[{ label: 'Subscription' }, { label: trans('subscription.subscription') }]"
            :description="trans('subscription.module_info_title')"
        >        </sip-page-intro>

        <!-- 1. Current Subscription -->
        <section class="sip-subscription-current" aria-label="Current subscription">
            <div class="sip-subscription-current__head">
                <div class="sip-subscription-current__status">
                    <span
                        class="sip-subscription-status-icon"
                        :class="instance.status === 'running' ? 'is-live' : 'is-expired'"
                        aria-hidden="true"
                    >
                        <em :class="instance.status === 'running' ? 'icon ni ni-check-circle-fill' : 'icon ni ni-cross-circle-fill'"></em>
                    </span>
                    <div>
                        <div class="sip-subscription-current__title-row">
                            <h4 class="sip-subscription-current__plan">{{ instance.plan.name }}</h4>
                            <span class="sip-badge" :class="instance.status === 'running' ? 'badge-success' : 'badge-danger'">
                                {{ trans('subscription.' + (instance.status === 'running' ? 'live' : 'expired')) }}
                            </span>
                            <span v-if="instance.is_trial" class="sip-badge badge-warning">{{ trans('subscription.trial') }}</span>
                        </div>
                        <p class="sip-subscription-current__price">{{ getPrice }}</p>
                        <p class="sip-subscription-current__expiry">
                            {{ trans('subscription.till') }} {{ instance.date_of_expiry | moment }}
                        </p>
                    </div>
                </div>
                <div class="sip-subscription-current__actions">
                    <button
                        type="button"
                        class="sip-subscription-btn sip-subscription-btn--primary"
                        :class="{ 'is-active': activeFlow === 'extend' }"
                        @click="openExtendFlow"
                    >
                        <em class="icon ni ni-calendar-check"></em>
                        {{ trans('subscription.extend_subscription') }}
                    </button>
                    <button
                        type="button"
                        class="sip-subscription-btn sip-subscription-btn--secondary"
                        :class="{ 'is-active': activeFlow === 'upgrade' }"
                        @click="openUpgradeFlow"
                    >
                        <em class="icon ni ni-arrow-up-circle"></em>
                        <span v-if="instance.status === 'running'">{{ trans('subscription.upgrade') }}</span>
                        <span v-else>{{ trans('subscription.choose_plan') }}</span>
                    </button>
                </div>
            </div>

            <div class="sip-subscription-current__body">
                <div class="sip-subscription-metric">
                    <span class="sip-subscription-metric__label">{{ trans('subscription.student_limit') }}</span>
                    <div class="sip-subscription-metric__value">
                        <strong>{{ student_count }}</strong>
                        <span>/ {{ instance.max_student }} ({{ getStudentLimit }}%)</span>
                    </div>
                    <div class="sip-subscription-progress">
                        <div
                            class="sip-subscription-progress__bar"
                            :class="'is-' + getProgressColor(getStudentLimit)"
                            role="progressbar"
                            :style="{ width: getStudentLimit + '%' }"
                            :aria-valuenow="getStudentLimit"
                            aria-valuemin="0"
                            aria-valuemax="100"
                        ></div>
                    </div>
                </div>

                <dl class="sip-subscription-details">
                    <div class="sip-subscription-details__item">
                        <dt>{{ trans('subscription.domain') }}</dt>
                        <dd>{{ instance.domain }}</dd>
                    </div>
                    <div class="sip-subscription-details__item">
                        <dt>{{ trans('subscription.email') }}</dt>
                        <dd>{{ instance.email }}</dd>
                    </div>
                    <div class="sip-subscription-details__item">
                        <dt>{{ trans('subscription.created_at') }}</dt>
                        <dd>{{ instance.created_at | momentDateTime }}</dd>
                    </div>
                    <div class="sip-subscription-details__item">
                        <dt>{{ trans('subscription.updated_at') }}</dt>
                        <dd>{{ instance.updated_at | momentDateTime }}</dd>
                    </div>
                </dl>
            </div>
        </section>

        <!-- 2. Extend / Renew flow -->
        <section
            v-show="activeFlow === 'extend'"
            class="sip-subscription-flow"
            aria-label="Extend or renew subscription"
        >
            <div class="sip-subscription-section-head">
                <div>
                    <h4 class="sip-subscription-section-head__title">{{ trans('subscription.extend_subscription') }}</h4>
                    <p class="sip-subscription-section-head__sub">{{ trans('subscription.extend_renew_subtitle') }}</p>
                </div>
                <button type="button" class="sip-subscription-flow__close" @click="closeFlow" aria-label="Close">
                    <em class="icon ni ni-cross"></em>
                </button>
            </div>

            <div class="sip-subscription-flow__toolbar">
                <div class="sip-subscription-flow__toolbar-group">
                    <span class="sip-subscription-flow__toolbar-label">{{ trans('subscription.frequency') }}</span>
                    <div class="sip-subscription-toggle-group sip-subscription-toggle-group--with-badge">
                        <button
                            type="button"
                            class="sip-subscription-toggle"
                            :class="{ 'is-active': subscriptionForm.frequency === 'monthly' }"
                            @click="changeFrequency('monthly')"
                        >{{ trans('subscription.monthly') }}</button>
                        <button
                            type="button"
                            class="sip-subscription-toggle"
                            :class="{ 'is-active': subscriptionForm.frequency === 'annually' }"
                            @click="changeFrequency('annually')"
                        >
                            {{ trans('subscription.annually') }}
                            <span
                                v-if="extendAnnualSavings"
                                class="sip-subscription-savings-badge"
                                :title="getAnnualSavingsAmountLabel(extendAnnualSavings)"
                            >{{ getAnnualSavingsBadgeLabel(extendAnnualSavings) }}</span>
                        </button>
                    </div>
                </div>
                <div class="sip-subscription-flow__toolbar-group">
                    <span class="sip-subscription-flow__toolbar-label">{{ trans('subscription.currency') }}</span>
                    <div class="sip-subscription-toggle-group">
                        <button
                            v-for="currency in currencies"
                            :key="currency.id"
                            type="button"
                            class="sip-subscription-toggle sip-subscription-toggle--compact"
                            :class="{ 'is-active': currency.id == selected_currency.id }"
                            @click="changeCurrency(currency)"
                        >{{ currency.name }}</button>
                    </div>
                </div>
            </div>

            <div class="sip-subscription-kpis">
                <div class="sip-subscription-kpi">
                    <span class="sip-subscription-kpi__label">{{ trans('subscription.plan') }}</span>
                    <span class="sip-subscription-kpi__value">{{ instance.plan.name }}</span>
                </div>
                <div class="sip-subscription-kpi sip-subscription-kpi--accent">
                    <span class="sip-subscription-kpi__label">{{ trans('subscription.payable') }}</span>
                    <span class="sip-subscription-kpi__value">{{ formatMultiCurrency(subscriptionForm.amount, selected_currency) }}</span>
                </div>
                <div class="sip-subscription-kpi">
                    <span class="sip-subscription-kpi__label">{{ trans('subscription.validity') }}</span>
                    <span class="sip-subscription-kpi__value">{{ subscriptionForm.new_validity | moment }}</span>
                </div>
            </div>

            <div class="sip-subscription-payment">
                <form class="sip-subscription-payment__form" @submit.prevent="proceed" @keydown="subscriptionForm.errors.clear($event.target.name)">
                    <div class="sip-subscription-payment__methods" v-if="hasPaymentMethods">
                        <label class="sip-subscription-payment__methods-label">Payment method</label>
                        <div class="sip-subscription-method-grid">
                            <label v-if="config.subscription_paypal" class="sip-subscription-method">
                                <input type="radio" value="paypal" v-model="subscriptionForm.method" name="method" @click="subscriptionForm.errors.clear('method')">
                                <span>PayPal</span>
                            </label>
                            <label v-if="config.subscription_stripe" class="sip-subscription-method">
                                <input type="radio" value="stripe" v-model="subscriptionForm.method" name="method" @click="subscriptionForm.errors.clear('method')">
                                <span>Stripe</span>
                            </label>
                            <label v-if="config.subscription_two_checkout" class="sip-subscription-method">
                                <input type="radio" value="two_checkout" v-model="subscriptionForm.method" name="method" @click="subscriptionForm.errors.clear('method')">
                                <span>2Checkout</span>
                            </label>
                            <label v-if="config.subscription_razorpay && selected_currency.name == 'INR' && razorpay_loaded" class="sip-subscription-method">
                                <input type="radio" value="razorpay" v-model="subscriptionForm.method" name="method" @click="subscriptionForm.errors.clear('method')">
                                <span>Razorpay</span>
                            </label>
                        </div>
                        <show-error :form-name="subscriptionForm" prop-name="method"></show-error>
                    </div>

                    <div v-if="subscriptionForm.method">
                        <div class="row" v-if="subscriptionForm.method === 'stripe'">
                            <div class="col-12">
                                <div class="form-group">
                                    <input class="form-control" type="number" maxlength="16" v-model="card_number" :placeholder="trans('subscription.card_number')">
                                </div>
                            </div>
                            <div class="col-3">
                                <div class="form-group">
                                    <input class="form-control" type="number" v-model="month" :placeholder="trans('subscription.month')">
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="form-group">
                                    <input class="form-control" type="number" v-model="year" :placeholder="trans('subscription.year')">
                                </div>
                            </div>
                            <div class="col-1"></div>
                            <div class="col-4">
                                <div class="form-group">
                                    <input class="form-control" type="number" v-model="cvc" :placeholder="trans('subscription.cvc')">
                                </div>
                            </div>
                        </div>
                        <div class="sip-subscription-card-element" v-show="subscriptionForm.method == 'two_checkout'">
                            <div id="card-element"></div>
                        </div>
                        <div class="sip-subscription-payment__actions">
                            <button type="submit" class="sip-subscription-btn sip-subscription-btn--primary sip-subscription-btn--proceed" v-if="button">
                                <em class="icon ni ni-arrow-right"></em>
                                {{ trans('general.proceed') }}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </section>

        <!-- 3. Upgrade Plans -->
        <section
            ref="upgradePlansSection"
            class="sip-subscription-plans"
            aria-label="Upgrade plans"
        >
            <div class="sip-subscription-section-head">
                <div>
                    <h4 class="sip-subscription-section-head__title">{{ trans('subscription.upgrade') }}</h4>
                    <p class="sip-subscription-section-head__sub">{{ trans('subscription.upgrade_section_subtitle') }}</p>
                </div>
                <div class="sip-subscription-flow__toolbar sip-subscription-flow__toolbar--inline">
                    <div class="sip-subscription-flow__toolbar-group">
                        <span class="sip-subscription-flow__toolbar-label">{{ trans('subscription.frequency') }}</span>
                        <div class="sip-subscription-toggle-group sip-subscription-toggle-group--with-badge">
                            <button
                                type="button"
                                class="sip-subscription-toggle"
                                :class="{ 'is-active': subscriptionForm.frequency === 'monthly' }"
                                @click="changeFrequency('monthly')"
                            >{{ trans('subscription.monthly') }}</button>
                            <button
                                type="button"
                                class="sip-subscription-toggle"
                                :class="{ 'is-active': subscriptionForm.frequency === 'annually' }"
                                @click="changeFrequency('annually')"
                            >
                                {{ trans('subscription.annually') }}
                                <span
                                    v-if="upgradeAnnualSavings"
                                    class="sip-subscription-savings-badge"
                                    :title="getAnnualSavingsAmountLabel(upgradeAnnualSavings)"
                                >{{ getAnnualSavingsBadgeLabel(upgradeAnnualSavings) }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="hasUpgradePlans" class="sip-subscription-plans__grid">
                <article
                    v-for="plan in normalizedUpgradablePlans"
                    :key="plan.id"
                    class="sip-subscription-plan-card"
                    :class="{
                        'is-current': plan.id === instance.plan_id,
                        'is-selected': subscriptionForm.upgrade_plan_id == plan.id
                    }"
                >
                    <div class="sip-subscription-plan-card__head">
                        <h5 class="sip-subscription-plan-card__name">{{ plan.name }}</h5>
                        <span v-if="plan.id === instance.plan_id" class="sip-badge badge-info">Current</span>
                    </div>
                    <div class="sip-subscription-plan-card__price">
                        <span class="sip-subscription-plan-card__amount">{{ getPlanPrice(plan, subscriptionForm.frequency) }}</span>
                    </div>
                    <ul class="sip-subscription-plan-card__features">
                        <li>
                            <em class="icon ni ni-check-circle-fill"></em>
                            {{ getPlanStudentLimit(plan) }} students
                        </li>
                        <li>
                            <em class="icon ni ni-check-circle-fill"></em>
                            {{ trans('subscription.frequency') }}: {{ trans('subscription.' + subscriptionForm.frequency) }}
                        </li>
                    </ul>
                    <button
                        type="button"
                        class="sip-subscription-btn"
                        :class="plan.id === instance.plan_id ? 'sip-subscription-btn--secondary' : 'sip-subscription-btn--primary'"
                        :disabled="plan.id === instance.plan_id"
                        @click="selectUpgradePlan(plan.id)"
                    >
                        <span v-if="plan.id === instance.plan_id">Current plan</span>
                        <span v-else>{{ trans('subscription.upgrade') }}</span>
                    </button>
                </article>
            </div>

            <div v-else class="sip-subscription-plans__empty sip-empty">
                <div class="sip-empty__well">
                    <em class="icon ni ni-arrow-up-circle"></em>
                </div>
                <p class="sip-empty__title">{{ trans('subscription.upgrade_empty_title') }}</p>
                <p class="sip-empty__hint">{{ trans('subscription.upgrade_contact_hint') }}</p>
                <div class="sip-empty__action" v-if="supportEmail">
                    <a
                        :href="'mailto:' + supportEmail + '?subject=' + encodeURIComponent(trans('subscription.upgrade'))"
                        class="sip-subscription-btn sip-subscription-btn--primary"
                    >
                        <em class="icon ni ni-mail"></em>
                        {{ trans('subscription.contact_support') }}
                    </a>
                </div>
            </div>

            <!-- Upgrade payment panel -->
            <div
                v-show="activeFlow === 'upgrade' && subscriptionForm.upgrade_plan_id"
                class="sip-subscription-flow sip-subscription-flow--nested"
            >
                <div class="sip-subscription-kpis">
                    <div class="sip-subscription-kpi">
                        <span class="sip-subscription-kpi__label">{{ trans('subscription.plan') }}</span>
                        <span class="sip-subscription-kpi__value">{{ selectedUpgradePlanName }}</span>
                    </div>
                    <div class="sip-subscription-kpi sip-subscription-kpi--accent">
                        <span class="sip-subscription-kpi__label">{{ trans('subscription.payable') }}</span>
                        <span class="sip-subscription-kpi__value">{{ formatMultiCurrency(subscriptionForm.amount, selected_currency) }}</span>
                    </div>
                    <div class="sip-subscription-kpi">
                        <span class="sip-subscription-kpi__label">{{ trans('subscription.validity') }}</span>
                        <span class="sip-subscription-kpi__value">{{ subscriptionForm.new_validity | moment }}</span>
                    </div>
                </div>

                <div class="sip-subscription-payment">
                    <form class="sip-subscription-payment__form" @submit.prevent="proceed" @keydown="subscriptionForm.errors.clear($event.target.name)">
                        <div class="sip-subscription-payment__methods" v-if="hasPaymentMethods">
                            <label class="sip-subscription-payment__methods-label">Payment method</label>
                            <div class="sip-subscription-method-grid">
                                <label v-if="config.subscription_paypal" class="sip-subscription-method">
                                    <input type="radio" value="paypal" v-model="subscriptionForm.method" name="method_upgrade" @click="subscriptionForm.errors.clear('method')">
                                    <span>PayPal</span>
                                </label>
                                <label v-if="config.subscription_stripe" class="sip-subscription-method">
                                    <input type="radio" value="stripe" v-model="subscriptionForm.method" name="method_upgrade" @click="subscriptionForm.errors.clear('method')">
                                    <span>Stripe</span>
                                </label>
                                <label v-if="config.subscription_two_checkout" class="sip-subscription-method">
                                    <input type="radio" value="two_checkout" v-model="subscriptionForm.method" name="method_upgrade" @click="subscriptionForm.errors.clear('method')">
                                    <span>2Checkout</span>
                                </label>
                                <label v-if="config.subscription_razorpay && selected_currency.name == 'INR' && razorpay_loaded" class="sip-subscription-method">
                                    <input type="radio" value="razorpay" v-model="subscriptionForm.method" name="method_upgrade" @click="subscriptionForm.errors.clear('method')">
                                    <span>Razorpay</span>
                                </label>
                            </div>
                            <show-error :form-name="subscriptionForm" prop-name="method"></show-error>
                        </div>

                        <div v-if="subscriptionForm.method">
                            <div class="row" v-if="subscriptionForm.method === 'stripe'">
                                <div class="col-12">
                                    <div class="form-group">
                                        <input class="form-control" type="number" maxlength="16" v-model="card_number" :placeholder="trans('subscription.card_number')">
                                    </div>
                                </div>
                                <div class="col-3">
                                    <div class="form-group">
                                        <input class="form-control" type="number" v-model="month" :placeholder="trans('subscription.month')">
                                    </div>
                                </div>
                                <div class="col-4">
                                    <div class="form-group">
                                        <input class="form-control" type="number" v-model="year" :placeholder="trans('subscription.year')">
                                    </div>
                                </div>
                                <div class="col-1"></div>
                                <div class="col-4">
                                    <div class="form-group">
                                        <input class="form-control" type="number" v-model="cvc" :placeholder="trans('subscription.cvc')">
                                    </div>
                                </div>
                            </div>
                            <div class="sip-subscription-payment__actions">
                                <button type="submit" class="sip-subscription-btn sip-subscription-btn--primary sip-subscription-btn--proceed" v-if="button">
                                    <em class="icon ni ni-arrow-right"></em>
                                    {{ trans('general.proceed') }}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </section>

        <!-- Hidden card element mount for 2Checkout when panel is collapsed -->
        <div class="sip-subscription-card-element sip-subscription-card-element--hidden" v-if="config.subscription_two_checkout && activeFlow !== 'extend'">
            <div id="card-element-fallback"></div>
        </div>

        <!-- 4. Subscription History -->
        <section
            class="sip-subscription-history"
            :class="{ 'is-empty': !displaySubscriptions.total }"
            aria-label="Subscription history"
        >
            <div class="sip-subscription-section-head">
                <div>
                    <h4 class="sip-subscription-section-head__title">{{ trans('subscription.history') }}</h4>
                    <p class="sip-subscription-section-head__sub" v-if="displaySubscriptions.total">
                        {{ trans('general.total_result_found', { count: displaySubscriptions.total, from: displaySubscriptions.from, to: displaySubscriptions.to }) }}
                    </p>
                </div>
            </div>

            <p v-if="showDemoHistoryBanner" class="sip-subscription-history__demo-banner">
                <span class="sip-badge badge-warning">{{ trans('subscription.test_data') }}</span>
                {{ trans('subscription.test_data_banner') }}
            </p>

            <div class="table-responsive sip-subscription-history__table" v-if="displaySubscriptions.total">
                <table class="table">
                    <thead>
                        <tr>
                            <th>{{ trans('subscription.invoice_number') }}</th>
                            <th>{{ trans('subscription.plan') }}</th>
                            <th>{{ trans('subscription.amount') }}</th>
                            <th>{{ trans('subscription.validity') }}</th>
                            <th>{{ trans('subscription.method') }}</th>
                            <th>{{ trans('subscription.date') }}</th>
                            <th>Status</th>
                            <th class="text-end">{{ trans('general.action') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="subscription in displaySubscriptions.data"
                            :key="subscription.uuid || subscription.id"
                            :class="{ 'is-demo-row': isDemoSubscription(subscription) }"
                        >
                            <td>
                                <span>{{ getNumber(subscription) }}</span>
                                <span v-if="isDemoSubscription(subscription)" class="sip-badge badge-warning sip-subscription-history__demo-badge">{{ trans('subscription.test_data') }}</span>
                            </td>
                            <td>{{ subscription.plan.name }}</td>
                            <td>{{ formatMultiCurrency(subscription.amount, subscription.currency) }}</td>
                            <td>{{ subscription.validity | moment }}</td>
                            <td>{{ toWord(subscription.source) }}</td>
                            <td>{{ subscription.created_at | moment }}</td>
                            <td>
                                <span class="sip-badge" :class="subscription.status ? 'badge-success' : 'badge-warning'">
                                    {{ subscription.status ? 'Paid' : 'Pending' }}
                                </span>
                            </td>
                            <td class="table-action">
                                <div
                                    v-if="canDownloadInvoice(subscription)"
                                    class="sip-row-actions sip-row-actions--icons"
                                >
                                    <a
                                        :href="invoicePdfUrl(subscription)"
                                        class="sip-row-actions__icon-btn"
                                        target="_blank"
                                        rel="noopener"
                                        :title="trans('subscription.download_invoice')"
                                        :aria-label="trans('subscription.download_invoice')"
                                    >
                                        <em class="icon ni ni-download"></em>
                                    </a>
                                    <a
                                        :href="invoicePrintUrl(subscription)"
                                        class="sip-row-actions__icon-btn"
                                        target="_blank"
                                        rel="noopener"
                                        :title="trans('general.print')"
                                        :aria-label="trans('general.print')"
                                    >
                                        <em class="icon ni ni-printer-fill"></em>
                                    </a>
                                </div>
                                <span v-else class="sip-subscription-history__no-action">—</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p class="sip-subscription-history__empty" v-if="!displaySubscriptions.total">
                No invoices yet. Renewals and upgrades will appear here.
            </p>

            <pagination-record
                v-if="displaySubscriptions.total && !usingDemoHistoryFallback"
                :page-length.sync="filterSubscriptionForm.page_length"
                :records="subscriptions"
                @updateRecords="getSubscriptions"
                @change.native="getSubscriptions"
            ></pagination-record>
        </section>
    </div>
</template>

<script>
	export default {
		data() {
			return {
                activeFlow: null,
				instance: {
                    plan_id: null,
					plan: {
						plan_price:[]
					},
					currency: {

					}
				},
                subscriptions: {
                    total: 0,
                    data: []
                },
                usingDemoHistoryFallback: false,
                showDemoHistoryBanner: false,
                filterSubscriptionForm: {
                    page_length: helper.getConfig('page_length')
                },
                subscriptionForm: new Form({
                    type: '',
                    frequency: 'monthly',
                    currency_id: '',
                    currency_name: '',
                    upgrade_plan_id: '',
                    new_validity: '',
                    amount: 0,
                    method: '2co',
                    address_line_1: '',
                    addres_line_2: '',
                    city: '',
                    state: '',
                    zipcode: '',
                    country: '',
                    phone: '',
                    stripeToken: '',
                    twoCheckoutToken: '',
                    razorpay_transaction_id: ''
                }),
                card_number: '',
                month: '',
                year: '',
                cvc: '',
                countries: [],
                config: {},
                currencies: [],
                student_count: 0,
                selected_currency: {},
                upgradable_plans: [],
                button: true,
                razorpay_loaded: 0,
                subscription_name: '',
                subscription_invoice_number_digit: 3,
                twoCheckoutComponent: {},
			}
		},
		mounted(){
            this.preRequisite();
            this.getSubscriptions();
            this.injectRazorpay();
		},
		methods: {
            preRequisite(){
                let loader = this.$loading.show()
                axios.get('/api/subscription/pre-requisite')
                    .then(response => {
                        this.instance = response.instance;
                        this.currencies = response.currencies;
                        this.upgradable_plans = response.upgradable_plans || [];
                        this.subscriptionForm.frequency = response.instance.frequency;
                        this.selected_currency = response.instance.currency;
                        this.subscriptionForm.currency_id = this.selected_currency.id;
                        this.student_count = response.student_count;
                        this.config = response.config;
                        this.countries = response.countries;
                        this.subscription_name = response.subscription_name;

                        this.subscriptionForm.address_line_1 = response.instance.address_line_1;
                        this.subscriptionForm.address_line_2 = response.instance.address_line_2;
                        this.subscriptionForm.city = response.instance.city;
                        this.subscriptionForm.state = response.instance.state;
                        this.subscriptionForm.zipcode = response.instance.zipcode;
                        this.subscriptionForm.country = response.instance.country || '';
                        this.subscriptionForm.phone = response.instance.phone;
                        this.subscription_invoice_number_digit = response.subscription_invoice_number_digit;

                        if (this.config.subscription_two_checkout) {
                            this.$nextTick(() => {
                                this.mountTwoCheckout();
                            });
                        }

                        loader.hide()
                    })
                    .catch(error => {
                        loader.hide()
                        helper.showDataErrorMsg(error);
                    })
            },
            mountTwoCheckout() {
                const target = document.getElementById('card-element') || document.getElementById('card-element-fallback');
                if (!target || !window.jsPaymentClient) {
                    return;
                }
                target.innerHTML = '';
                let component = jsPaymentClient.components.create('card');
                this.twoCheckoutComponent = component;
                component.mount(target.id === 'card-element' ? '#card-element' : '#card-element-fallback');
            },
            openExtendFlow() {
                this.activeFlow = 'extend';
                this.changeSubscription('extend');
            },
            openUpgradeFlow() {
                this.activeFlow = 'upgrade';
                this.changeSubscription('upgrade');
                this.$nextTick(() => {
                    const el = this.$refs.upgradePlansSection;
                    if (el && el.scrollIntoView) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                });
            },
            selectUpgradePlan(planId) {
                this.activeFlow = 'upgrade';
                this.subscriptionForm.type = 'upgrade';
                this.subscriptionForm.upgrade_plan_id = planId;
                this.calculate();
            },
            closeFlow() {
                this.activeFlow = null;
                this.subscriptionForm.type = '';
                this.subscriptionForm.upgrade_plan_id = '';
            },
            getPlanStudentLimit(plan) {
                const limit = plan.options && plan.options.max_student
                    ? plan.options.max_student
                    : (plan.max_student || '—');
                return limit;
            },
            getSubscriptions(page){
                let loader = this.$loading.show()
                if (typeof page !== 'number') {
                    page = 1;
                }
                let url = helper.getFilterURL(this.filterSubscriptionForm);
                axios.get('/api/subscription?page=' + page + url)
                    .then(response => {
                        loader.hide()
                        if (!response.total) {
                            this.subscriptions = this.buildDemoSubscriptionHistory();
                            this.usingDemoHistoryFallback = true;
                            this.showDemoHistoryBanner = true;
                        } else {
                            this.subscriptions = response;
                            this.usingDemoHistoryFallback = false;
                            this.showDemoHistoryBanner = (response.data || []).some(subscription => this.isDemoSubscription(subscription));
                        }
                    })
                    .catch(error => {
                        loader.hide()
                        helper.showDataErrorMsg(error);
                    });
            },
            // Demo subscription history — UUIDs match DemoSubscriptionHistorySeeder for working PDF downloads.
            buildDemoSubscriptionHistory() {
                const currency = { id: 1, name: 'INR', symbol: '₹', position: 'prefix', decimal_place: 2 };
                const prefix = 'Invoice-';
                const rows = [
                    {
                        id: 'demo-1',
                        uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567891',
                        prefix,
                        number: 2,
                        plan: { name: 'Sipinor One Plan' },
                        amount: 7500,
                        currency,
                        validity: moment().add(1, 'year').format('YYYY-MM-DD'),
                        source: 'stripe',
                        status: 1,
                        created_at: moment().subtract(6, 'months').format('YYYY-MM-DD'),
                        options: { is_demo: true },
                    },
                    {
                        id: 'demo-2',
                        uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567892',
                        prefix,
                        number: 3,
                        plan: { name: 'Sipinor Premium Plan' },
                        amount: 1150,
                        currency,
                        validity: moment().add(3, 'months').format('YYYY-MM-DD'),
                        source: 'razorpay',
                        status: 1,
                        created_at: moment().subtract(9, 'months').format('YYYY-MM-DD'),
                        options: { is_demo: true },
                    },
                    {
                        id: 'demo-3',
                        uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567893',
                        prefix,
                        number: 4,
                        plan: { name: 'Sipinor Growth Plan' },
                        amount: 18500,
                        currency,
                        validity: moment().add(8, 'months').format('YYYY-MM-DD'),
                        source: 'paypal',
                        status: 1,
                        created_at: moment().subtract(14, 'months').format('YYYY-MM-DD'),
                        options: { is_demo: true },
                    },
                    {
                        id: 'demo-4',
                        uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567894',
                        prefix,
                        number: 5,
                        plan: { name: 'Sipinor One Plan' },
                        amount: 750,
                        currency,
                        validity: moment().add(1, 'month').format('YYYY-MM-DD'),
                        source: 'two_checkout',
                        status: 1,
                        created_at: moment().subtract(2, 'months').format('YYYY-MM-DD'),
                        options: { is_demo: true },
                    },
                ];

                return {
                    total: rows.length,
                    from: 1,
                    to: rows.length,
                    data: rows,
                };
            },
            isDemoSubscription(subscription) {
                if (!subscription) {
                    return false;
                }

                if (this.usingDemoHistoryFallback) {
                    return true;
                }

                const options = subscription.options;
                if (!options) {
                    return false;
                }

                if (typeof options === 'string') {
                    try {
                        return !!JSON.parse(options).is_demo;
                    } catch (e) {
                        return false;
                    }
                }

                return !!options.is_demo;
            },
            getProgressColor(number){
                if(number < 25)
                    return 'success';
                else if(number < 50)
                    return 'info';
                else if(number < 75)
                    return 'warning';
                else
                    return 'danger';
            },
            changeSubscription(type){
                this.subscriptionForm.type = type;
                this.calculate();
            },
            changeCurrency(currency){
                this.selected_currency = currency;
                this.subscriptionForm.currency_id = currency.id;
                this.subscriptionForm.currency_name = currency.name;
                this.calculate();
            },
            changeFrequency(frequency){
                this.subscriptionForm.frequency = frequency;
                this.calculate();
            },
            getNumber(subscription){
                return (subscription.number) ?  subscription.prefix+''+helper.formatWithPadding(subscription.number,this.subscription_invoice_number_digit) : '';
            },
            getPlanPrice(plan, frequency){
                let price = plan.plan_price.find(o => o.currency_id == this.selected_currency.id && o.frequency === frequency);
                if (!price) {
                    return '—';
                }
                return helper.formatMultiCurrency(price.amount, this.selected_currency)+' /'+(frequency === 'monthly' ? 'Mo' : 'Yr');
            },
            getAnnualSavings(plan) {
                if (!plan || !Array.isArray(plan.plan_price) || !this.selected_currency || !this.selected_currency.id) {
                    return null;
                }

                const monthly = plan.plan_price.find(o => o.currency_id == this.selected_currency.id && o.frequency === 'monthly');
                const annual = plan.plan_price.find(o => o.currency_id == this.selected_currency.id && o.frequency === 'annually');

                if (!monthly || !annual || !monthly.amount || !annual.amount) {
                    return null;
                }

                const monthlyAnnualized = Number(monthly.amount) * 12;
                const annualAmount = Number(annual.amount);

                if (monthlyAnnualized <= annualAmount) {
                    return null;
                }

                const amountSaved = monthlyAnnualized - annualAmount;
                const percent = Math.round((amountSaved / monthlyAnnualized) * 100);

                if (percent < 1) {
                    return null;
                }

                return {
                    percent,
                    amount: amountSaved,
                    amountFormatted: helper.formatMultiCurrency(amountSaved, this.selected_currency)
                };
            },
            getAnnualSavingsBadgeLabel(savings) {
                if (!savings) {
                    return '';
                }

                if (savings.percent >= 5) {
                    return this.trans('subscription.annual_savings_badge', { percent: savings.percent });
                }

                return this.trans('subscription.annual_savings_amount', { amount: savings.amountFormatted });
            },
            getAnnualSavingsAmountLabel(savings) {
                if (!savings) {
                    return '';
                }

                return this.trans('subscription.annual_savings_amount', { amount: savings.amountFormatted });
            },
            toWord(word){
                return helper.toWord(word);
            },
            calculate(){
                if (this.subscriptionForm.type === 'upgrade') {
                    this.subscriptionForm.new_validity = (this.instance.date_of_expiry > moment().format('YYYY-MM-DD')) ? this.instance.date_of_expiry : moment().add(1, (this.subscriptionForm.frequency === 'monthly' ? 'M' : 'y'));

                    if(!this.subscriptionForm.upgrade_plan_id)
                        this.subscriptionForm.amount = 0;
                    else {
                        let selected_upgrade_plan = this.normalizedUpgradablePlans.find(o => o.id == this.subscriptionForm.upgrade_plan_id);
                        let frequency = (this.instance.date_of_expiry > moment().format('YYYY-MM-DD')) ? moment(this.instance.date_of_expiry).diff(moment(), 'months') ? 'annually' : 'monthly' : this.subscriptionForm.frequency;
                        let days_remaining = moment(this.instance.date_of_expiry).diff(moment(), 'days') + 1;
                        let selected_upgrade_plan_price = selected_upgrade_plan.plan_price.find(o => o.currency_id == this.selected_currency.id && o.frequency === frequency);
                        this.subscriptionForm.amount = (this.instance.date_of_expiry > moment().format('YYYY-MM-DD')) ? helper.formatNumber((selected_upgrade_plan_price.amount/(frequency === 'monthly' ? 30 : 365)) * days_remaining) : helper.formatNumber(selected_upgrade_plan_price.amount);
                    }
                } else if (this.subscriptionForm.type === 'extend') {
                    this.subscriptionForm.new_validity = (this.instance.date_of_expiry > moment().format('YYYY-MM-DD')) ? moment(this.instance.date_of_expiry).add(1, (this.subscriptionForm.frequency === 'monthly' ? 'M' : 'y')) : moment().add(1, (this.subscriptionForm.frequency === 'monthly' ? 'M' : 'y'));
                    let price = this.instance.plan.plan_price.find(o => o.currency_id == this.selected_currency.id && o.frequency === this.subscriptionForm.frequency);
                    this.subscriptionForm.amount = helper.formatNumber(price.amount);
                }
            },
            formatMultiCurrency(amount, currency){
                return helper.formatMultiCurrency(amount, currency);
            },
            canDownloadInvoice(subscription) {
                const token = this.authToken || helper.getAuthToken();
                return subscription && subscription.uuid && !!subscription.status && !!token;
            },
            invoicePdfUrl(subscription) {
                const token = this.authToken || helper.getAuthToken();
                if (!token) {
                    return '';
                }

                return `/subscription/${subscription.uuid}/pdf?token=${encodeURIComponent(token)}`;
            },
            invoicePrintUrl(subscription) {
                const token = this.authToken || helper.getAuthToken();
                if (!token) {
                    return '';
                }

                return `/subscription/${subscription.uuid}/print?token=${encodeURIComponent(token)}`;
            },
            injectStripe() {
                let self = this;
                let el = document.createElement('SCRIPT');
                let ctr = 0;
                let scriptSource = 'https://js.stripe.com/v2';
                let scripts = document.getElementsByTagName('script');
                let scriptExists = false;
                for (var i in scripts){
                    if (scripts[i].src === scriptSource) {
                        scriptExists = true;
                    }
                }
                el.setAttribute('src', scriptSource);
                if(!scriptExists) {
                    document.head.appendChild(el);
                }

                return new Promise((resolve, reject) => {
                    let handle = window.setInterval(function () {
                        if (window.Stripe) {
                            self.loaded = true;
                            resolve();
                            clearInterval(handle);
                        }
                        ctr++;
                        if (ctr > 1000) {
                            reject("vue-stripe: Unable to load stripe.js");
                            clearInterval(handle);
                        }
                    }, 5);
                });
            },
            proceed(){
                this.button = false;
                NioApp.Toast(i18n.subscription.payment_processing, 'success');
                if(this.subscriptionForm.method === 'stripe')
                    this.stripeCheckout();
                else if(this.subscriptionForm.method === 'razorpay')
                    this.razorpayCheckout();
                else if(this.subscriptionForm.method === 'two_checkout')
                    this.twoCheckout();
                else
                    this.paypalCheckout();
            },
            stripeCheckout(){
                this.injectStripe()
                    .then(() => {
                        Stripe.setPublishableKey(this.config.subscription_stripe_publishable_key);
                        Stripe.card.createToken({
                            number: this.card_number,
                            cvc: this.cvc,
                            exp_month: this.month,
                            exp_year: this.year
                        }, this.stripeResponseHandler);
                    })
                    .catch(e => {
                        NioApp.Toast('Could not load Stripe.js!', 'error');
                    });
            },
            stripeResponseHandler(status, response) {
                if(status == 200){
                    this.subscriptionForm.stripeToken = response.id;
                    this.subscriptionForm.post('/api/subscription/stripe')
                        .then(response => {
                            NioApp.Toast(response.message, 'success');
                            this.preRequisite();
                            this.getSubscriptions();
                            this.button = true;
                            this.closeFlow();
                        })
                        .catch(error => {
                            helper.showErrorMsg(error);
                            this.button = true;
                        })
                } else {
                    loader.hide();
                    NioApp.Toast(response.error.message, 'error');
                    this.button = true;
                }
            },
            paypalCheckout(){
                this.subscriptionForm.post('/api/subscription/paypal')
                    .then(response => {
                        window.location = response;
                    })
                    .catch(error => {
                        helper.showErrorMsg(error);
                        this.button = true;
                    })
            },
            injectRazorpay() {
                let vm = this;
                var result = $.Deferred(),
                script = document.createElement("script");

                script.async = "async";
                script.type = "text/javascript";
                script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                script.onload = script.onreadystatechange = function(_, isAbort) {
                    if (!script.readyState || /loaded|complete/.test(script.readyState)) {
                        if (isAbort)
                            result.reject();
                        else 
                            result.resolve();
                    }
                    vm.razorpay_loaded = 1;
                };

                script.onerror = function() {
                    result.reject();
                };

                document.head.appendChild(script);

                return result.promise();
            },
            razorpayCheckout(){
                let vm = this;
                var options = {
                    key: this.config.subscription_razorpay_publishable_key,
                    protocol: 'https',
                    hostname: 'api.razorpay.com',
                    amount: this.subscriptionForm.amount * 100,
                    name: helper.getProviderName(),
                    description: this.subscription_name,
                    image: helper.getProviderLogo(),
                    prefill: {
                        email: "",
                        contact: "",
                        name: ""
                    },
                    handler: function (transaction, response){
                        vm.razorpayResponseHandler(transaction.razorpay_payment_id)
                    }
                };

                window.rzpay = new Razorpay(options);
                rzpay.open();
            },
            razorpayResponseHandler(razorpay_transaction_id){
                let loader = this.$loading.show();
                this.subscriptionForm.razorpay_transaction_id = razorpay_transaction_id;
                this.subscriptionForm.post('/api/subscription/razorpay')
                    .then(response => {
                        loader.hide();
                        NioApp.Toast(response.message, 'success');
                        this.preRequisite();
                        this.getSubscriptions();
                        this.button = true;
                        this.closeFlow();
                    })
                    .catch(error => {
                        loader.hide();
                        helper.showErrorMsg(error);
                        this.button = true;
                    })
            },
            twoCheckout() {
                let loader = this.$loading.show()
                const billingDetails = {
                    name: helper.getConfig('institute_name')
                }

                jsPaymentClient.tokens.generate(this.twoCheckoutComponent, billingDetails).then((response) => {
                    this.subscriptionForm.twoCheckoutToken = response.token;
                    this.subscriptionForm.post('/api/subscription/twocheckout')
                        .then(response => {
                            loader.hide();
                            location.reload()
                        })
                        .catch(error => {
                            helper.showErrorMsg(error)
                            loader.hide();
                        })
                }).catch((error) => {
                    helper.showErrorMsg(error)
                    loader.hide();
                });
            }
		},
		computed: {
            authToken(){
                return helper.getAuthToken();
            },
			getPrice(){
				let price = this.instance.plan.plan_price.find(o => o.frequency === this.instance.frequency && o.currency_id == this.instance.currency.id);

				return (price) ? ('('+helper.formatMultiCurrency(price.amount, this.instance.currency)+' / '+ (price.frequency === 'monthly' ? 'Mo' : 'Yr') +')') : '';
			},
            getStudentLimit(){
                if (!this.instance.max_student) {
                    return 0;
                }
                return helper.formatNumber((this.student_count/this.instance.max_student)*100);
            },
            normalizedUpgradablePlans() {
                if (Array.isArray(this.upgradable_plans)) {
                    return this.upgradable_plans;
                }
                return Object.values(this.upgradable_plans || {});
            },
            hasUpgradePlans() {
                return this.normalizedUpgradablePlans.length > 0;
            },
            extendAnnualSavings() {
                return this.getAnnualSavings(this.instance.plan);
            },
            upgradeAnnualSavings() {
                const selectedPlan = this.subscriptionForm.upgrade_plan_id
                    ? this.normalizedUpgradablePlans.find(o => o.id == this.subscriptionForm.upgrade_plan_id)
                    : null;

                return this.getAnnualSavings(selectedPlan || this.instance.plan);
            },
            supportEmail() {
                return helper.getConfig('email') || '';
            },
            selectedUpgradePlanName() {
                const plan = this.normalizedUpgradablePlans.find(o => o.id == this.subscriptionForm.upgrade_plan_id);
                return plan ? plan.name : '';
            },
            hasPaymentMethods() {
                return this.config.subscription_paypal
                    || this.config.subscription_stripe
                    || this.config.subscription_two_checkout
                    || (this.config.subscription_razorpay && this.selected_currency.name == 'INR' && this.razorpay_loaded);
            },
            displaySubscriptions() {
                return this.subscriptions;
            },
		},
        filters: {
          moment(date) {
            return helper.formatDate(date);
          },
          momentDateTime(datetime) {
            return helper.formatDateTime(datetime);
          }
        },
        watch: {
            activeFlow(val) {
                if (val === 'extend' && this.config.subscription_two_checkout) {
                    this.$nextTick(() => this.mountTwoCheckout());
                }
            }
        }
	}
</script>
